import React, { useState, useEffect } from 'react';
import IconPicker from './IconPicker';

const DailyWordsAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Icon picker state
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [availableIcons, setAvailableIcons] = useState([]);
  const [iconSearchQuery, setIconSearchQuery] = useState('');
  const [iconPickerTarget, setIconPickerTarget] = useState('');

  const authHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${adminToken}`
  };

  // Load settings
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await fetch(`${API}/admin/daily-words/settings`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
      }
    } catch (err) {
      showNotification('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };

  const saveField = async (fields) => {
    try {
      const res = await fetch(`${API}/admin/daily-words/settings`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify(fields)
      });
      if (res.ok) {
        setSettings(prev => ({ ...prev, ...fields }));
        showNotification('Saved!', 'success');
      } else {
        showNotification('Failed to save', 'error');
      }
    } catch {
      showNotification('Failed to save', 'error');
    }
  };

  const openIconPicker = async (target) => {
    setIconPickerTarget(target);
    setIconSearchQuery('');
    try {
      const res = await fetch(`${API}/icons`, { headers: { 'Authorization': `Bearer ${adminToken}` } });
      if (res.ok) {
        const data = await res.json();
        setAvailableIcons(data.icons || []);
      }
    } catch {}
    setIconPickerOpen(true);
  };

  const handleIconSelect = async (icon) => {
    setIconPickerOpen(false);
    const field = iconPickerTarget;
    await saveField({ [field]: icon.url });
  };

  const clearIcon = async (field) => {
    await saveField({ [field]: '' });
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleNumberChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: parseInt(value) || 0 }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!settings) return null;

  const cardClass = `rounded-lg border p-4 mb-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`;
  const labelClass = `block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;
  const inputClass = `w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;
  const btnClass = `px-3 py-1.5 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'}`;
  const pickBtnClass = `px-2 py-1 text-xs rounded font-medium ${isDarkMode ? 'bg-purple-700 text-white hover:bg-purple-600' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'}`;
  const clearBtnClass = `px-2 py-1 text-xs rounded font-medium ${isDarkMode ? 'bg-red-700 text-white hover:bg-red-600' : 'bg-red-100 text-red-700 hover:bg-red-200'}`;

  const textFields = [
    // Page Header
    { section: 'Page Header', fields: [
      { label: 'Page Title', field: 'page_title', placeholder: 'Daily Words' },
    ]},
    // Widget
    { section: 'Widget (Today\'s Words)', fields: [
      { label: 'Widget Title', field: 'widget_title', placeholder: 'Today\'s Learning Words' },
      { label: 'Daily Goal Label', field: 'daily_goal_label', placeholder: 'Daily Goal' },
      { label: 'Words Learned Label', field: 'words_learned_label', placeholder: 'words learned today' },
      { label: 'Remind Me Button', field: 'remind_me_text', placeholder: 'Remind Me' },
      { label: 'Loading Text', field: 'loading_text', placeholder: 'Loading your words...' },
    ]},
    // Notification Popup
    { section: 'Notification Popup', fields: [
      { label: 'Popup Title', field: 'notification_title', placeholder: 'New Words Today!' },
      { label: 'Popup Subtitle', field: 'notification_subtitle', placeholder: 'Words are waiting for you below' },
      { label: 'Popup Button Text', field: 'notification_button', placeholder: 'Start Learning' },
    ], numbers: [
      { label: 'Popup Delay', field: 'notification_delay_ms', min: 500, max: 30000, unit: 'ms' },
    ]},
    // Celebration Modal
    { section: 'Celebration Modal', fields: [
      { label: 'Celebration Title', field: 'celebration_title', placeholder: 'Goal Achieved!' },
      { label: 'Celebration Subtitle', field: 'celebration_subtitle', placeholder: 'You learned your daily words!' },
      { label: 'Celebration Button', field: 'celebration_button', placeholder: 'Continue Learning' },
      { label: 'Keep Up Text', field: 'keep_up_text', placeholder: 'Keep up the amazing work!' },
    ], numbers: [
      { label: 'Auto-dismiss Time', field: 'celebration_dismiss_ms', min: 1000, max: 30000, unit: 'ms' },
    ]},
    // Goal Complete
    { section: 'Goal Complete', fields: [
      { label: 'Goal Complete Text', field: 'goal_complete_text', placeholder: 'Goal Achieved! Great job today!' },
      { label: 'Come Back Text', field: 'come_back_text', placeholder: 'Come back tomorrow for more words!' },
      { label: 'Words Reset Text', field: 'words_reset_text', placeholder: 'Words reset daily at midnight' },
      { label: 'More To Goal Text', field: 'more_to_goal_text', placeholder: 'more to reach your goal!' },
    ]},
    // No Words State
    { section: 'No Words State', fields: [
      { label: 'No Words Title', field: 'no_words_text', placeholder: 'No words available right now' },
      { label: 'Check Back Text', field: 'check_back_text', placeholder: 'Check back soon for new daily words!' },
    ]},
    // Info Box
    { section: 'Info Box', fields: [
      { label: 'Info Text', field: 'info_text', placeholder: 'Each day you learn a new word...' },
    ]},
    // Weekly Section
    { section: 'Weekly Section', fields: [
      { label: 'Weekly Title', field: 'weekly_title', placeholder: 'Your Learning This Week' },
      { label: 'Start Journey Text', field: 'start_journey_text', placeholder: 'Start your learning journey today!' },
      { label: 'Great Progress Text', field: 'great_progress_text', placeholder: 'Great progress! Keep it going!' },
      { label: 'Learned This Week', field: 'learned_this_week_text', placeholder: 'You learned {count} words this week! (use {count})' },
      { label: 'No Words This Week', field: 'no_words_week_text', placeholder: 'No words learned this week yet' },
      { label: 'Week Resets Text', field: 'week_resets_text', placeholder: 'Week resets every Sunday' },
    ]},
    // Pro Gate
    { section: 'Pro Gate Text', fields: [
      { label: 'Pro Feature Label', field: 'pro_feature_text', placeholder: 'Pro Feature' },
      { label: 'Unlock Text', field: 'unlock_text', placeholder: 'Unlock Daily Words with a Pro membership' },
      { label: 'Upgrade Button', field: 'upgrade_text', placeholder: 'Upgrade to Pro' },
      { label: 'Track Weekly Text', field: 'track_weekly_text', placeholder: 'Track your weekly learning progress' },
    ]},
    // Word Card Buttons
    { section: 'Word Card Buttons', fields: [
      { label: 'Learned Button', field: 'learned_button_text', placeholder: 'Learned' },
      { label: 'Mark Learned Button', field: 'mark_learned_text', placeholder: 'Mark as Learned' },
    ]},
    // Pro Tip
    { section: 'Pro Tip Section', fields: [
      { label: 'Pro Tip Title', field: 'pro_tip_title', placeholder: 'Pro Tip' },
      { label: 'Pro Tip Message', field: 'pro_tip_text', placeholder: 'Learning just a few words...' },
    ]},
  ];

  const iconFields = [
    { label: 'Page Header', field: 'header_icon_url', emoji: '\ud83d\udcda' },
    { label: 'Notification Popup', field: 'notification_icon_url', emoji: '\ud83c\udfaf' },
    { label: 'Widget', field: 'widget_icon_url', emoji: '\ud83c\udfaf' },
    { label: 'Weekly Calendar', field: 'weekly_icon_url', emoji: '\ud83d\udcc5' },
    { label: 'Info Box', field: 'info_icon_url', emoji: '\ud83d\udca1' },
    { label: 'Weekly Summary', field: 'summary_icon_url', emoji: '\ud83d\udcd8' },
    { label: 'Celebration', field: 'celebration_icon_url', emoji: '\ud83c\udf89' },
    { label: 'Goal Star', field: 'goal_star_icon_url', emoji: '\ud83c\udf1f' },
    { label: 'Pro Lock', field: 'pro_lock_icon_url', emoji: '\ud83d\udd12' },
    { label: 'Pro Tip', field: 'pro_tip_icon_url', emoji: '\ud83d\udca1' },
  ];

  const filteredIcons = availableIcons.filter(icon =>
    !iconSearchQuery || (icon.name || icon.url || '').toLowerCase().includes(iconSearchQuery.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Daily Words Admin</h2>

      {textFields.map((section) => (
        <div key={section.section} className={cardClass}>
          <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{section.section}</h3>
          {section.fields.map((f) => (
            <div key={f.field} className="mb-3">
              <label className={labelClass}>{f.label}</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  className={inputClass}
                  value={settings[f.field] || ''}
                  onChange={(e) => handleChange(f.field, e.target.value)}
                  placeholder={f.placeholder || ''}
                />
                <button className={btnClass} onClick={() => saveField({ [f.field]: settings[f.field] || '' })}>Save</button>
              </div>
            </div>
          ))}
          {(section.numbers || []).map((f) => (
            <div key={f.field} className="mb-3">
              <label className={labelClass}>{f.label} {f.unit && <span className="text-xs opacity-60">({f.unit})</span>}</label>
              <div className="flex gap-2 items-center">
                <input
                  type="number"
                  className={`${inputClass} w-32`}
                  value={settings[f.field] || 0}
                  min={f.min}
                  max={f.max}
                  onChange={(e) => handleNumberChange(f.field, e.target.value)}
                />
                <button className={btnClass} onClick={() => saveField({ [f.field]: settings[f.field] || 0 })}>Save</button>
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* Icons */}
      <div className={cardClass}>
        <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Icons</h3>
        <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Pick custom icons or leave empty for default emoji.</p>
        {iconFields.map((ic) => (
          <div key={ic.field} className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 flex items-center justify-center rounded-lg border ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'}`}>
              {settings[ic.field]
                ? <img src={`${API.replace('/api', '')}${settings[ic.field]}`} alt="" className="w-8 h-8 object-contain" />
                : <span className="text-xl">{ic.emoji}</span>}
            </div>
            <div className="flex-1">
              <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{ic.label}</span>
              {settings[ic.field] && (
                <p className={`text-xs truncate max-w-[200px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{settings[ic.field]}</p>
              )}
            </div>
            <button className={pickBtnClass} onClick={() => openIconPicker(ic.field)}>Pick</button>
            {settings[ic.field] && <button className={clearBtnClass} onClick={() => clearIcon(ic.field)}>Clear</button>}
          </div>
        ))}
      </div>

      {/* Icon Picker Modal — uses shared IconPicker (browses ALL Image Manager folders) */}
      {iconPickerOpen && (
        <IconPicker
          API={API}
          adminToken={adminToken}
          isDarkMode={isDarkMode}
          onClose={() => setIconPickerOpen(false)}
          onPick={async (url) => {
            setIconPickerOpen(false);
            const field = iconPickerTarget;
            await saveField({ [field]: url });
          }}
        />
      )}
    </div>
  );
};

export default DailyWordsAdmin;
